import type { TaskRecord } from "@nexus/contracts";

export interface TaskCounts {
  workingCount: number;
  failedCount: number;
  doneCount: number;
}

export type TaskUrgency = "working" | "failed" | "completed";

const SUMMARY_SEPARATOR = " · ";

/** Highest-urgency bucket present — working, then failed, then completed. */
export function getTaskUrgency({ workingCount, failedCount }: TaskCounts): TaskUrgency {
  if (workingCount > 0) return "working";
  if (failedCount > 0) return "failed";
  return "completed";
}

/**
 * Categorize active tasks into working / failed / done buckets.
 * A task is "failed" if any subtask reports an error, "working" if it has no
 * errors but at least one incomplete subtask, and "done" once every subtask is
 * complete with no errors.
 */
export function getTaskCounts(tasks: TaskRecord[]): TaskCounts {
  const workingCount = tasks.filter((t) =>
    !t.stages.some((s) => s.subtasks.some((st) => st.hasErrors)) &&
    t.stages.some((s) => s.subtasks.some((st) => st.progress < 1)),
  ).length;
  const failedCount = tasks.filter((t) =>
    t.stages.some((s) => s.subtasks.some((st) => st.hasErrors)),
  ).length;
  const doneCount = tasks.filter((t) =>
    !t.stages.some((s) => s.subtasks.some((st) => st.hasErrors)) &&
    t.stages.every((s) => s.subtasks.every((st) => st.progress === 1)),
  ).length;
  return { workingCount, failedCount, doneCount };
}

type Translate = (
  key: string,
  options: { count: number; defaultValue_one: string; defaultValue_other: string },
) => string;

/** Build the verbose summary, e.g. "2 working · 1 failed · 3 completed". */
export function formatTaskSummary(
  { workingCount, failedCount, doneCount }: TaskCounts,
  t: Translate,
): string {
  const parts: string[] = [];
  if (workingCount > 0) {
    parts.push(
      t("taskProgress.workingCount", {
        count: workingCount,
        defaultValue_one: "{{count}} working",
        defaultValue_other: "{{count}} working",
      }),
    );
  }
  if (failedCount > 0) {
    parts.push(
      t("taskProgress.failedCount", {
        count: failedCount,
        defaultValue_one: "{{count}} failed",
        defaultValue_other: "{{count}} failed",
      }),
    );
  }
  if (doneCount > 0) {
    parts.push(
      t("taskProgress.completedCount", {
        count: doneCount,
        defaultValue_one: "{{count}} completed",
        defaultValue_other: "{{count}} completed",
      }),
    );
  }
  return parts.join(SUMMARY_SEPARATOR);
}

export function isTaskFailed(task: TaskRecord): boolean {
  return task.stages.some((s) => s.subtasks.some((st) => st.hasErrors));
}

export function isTaskDone(task: TaskRecord): boolean {
  return (
    !isTaskFailed(task) &&
    task.stages.every((s) => s.subtasks.every((st) => st.progress === 1))
  );
}

export interface PartitionedTasks {
  inProgress: TaskRecord[];
  failed: TaskRecord[];
  done: TaskRecord[];
}

/** Working first, then failed, then done — each bucket keeps server order. */
export function partitionTasks(tasks: TaskRecord[]): PartitionedTasks {
  const inProgress: TaskRecord[] = [];
  const failed: TaskRecord[] = [];
  const done: TaskRecord[] = [];
  for (const task of tasks) {
    if (isTaskFailed(task)) failed.push(task);
    else if (isTaskDone(task)) done.push(task);
    else inProgress.push(task);
  }
  return { inProgress, failed, done };
}

export function sortTasksForSidebar(tasks: TaskRecord[]): TaskRecord[] {
  const { inProgress, failed, done } = partitionTasks(tasks);
  return [...inProgress, ...failed, ...done];
}
